/**
 * useWeeklyPanels Hook
 *
 * Manages loading and saving of weekly panel data to PostgreSQL.
 * Uses userSettingsService for persistence with localStorage fallback.
 *
 * Features:
 * - Auto-load on mount
 * - Debounced content saves (500ms)
 * - Debounced position saves (300ms)
 * - Optimistic updates
 */

import { useEffect, useRef, useCallback } from 'react';
import { useWeeklyPanelsStore } from '../store/useWeeklyPanelsStore';
import { getSetting, setSetting } from '@shared/services/userSettingsService';
import { logger } from '@shared/utils/logger';
import type { PanelType, Position, PanelState, PanelSize } from '../types/weeklyPanels';
import { WEEKLY_PANELS_NAMESPACE, WEEKLY_PANELS_KEYS, defaultPanelStates } from '../types/weeklyPanels';

/**
 * Debounce configuration
 */
const CONTENT_DEBOUNCE_MS = 500;
const POSITION_DEBOUNCE_MS = 300;

/**
 * Hook for managing weekly panels data
 */
export const useWeeklyPanels = () => {
  const store = useWeeklyPanelsStore();
  const contentDebounceRef = useRef<Record<string, NodeJS.Timeout>>({});
  const positionDebounceRef = useRef<NodeJS.Timeout | null>(null);
  // CRITICAL FIX: Use ref to track if load has been attempted to prevent infinite loops
  // The previous implementation had store in the dependency array which caused infinite re-renders
  const loadAttemptedRef = useRef(false);

  /**
   * Load all panel data from PostgreSQL
   * CRITICAL: This callback has NO dependencies to prevent infinite loop.
   * Uses getState() for fresh store values instead of closure over store.
   */
  const loadPanels = useCallback(async () => {
    // Get fresh state using getState() to avoid stale closures
    const currentState = useWeeklyPanelsStore.getState();

    // Skip if already initialized or load already attempted
    if (currentState.isInitialized || loadAttemptedRef.current) return;

    // Mark as attempted to prevent duplicate loads
    loadAttemptedRef.current = true;

    currentState.setLoading(true);

    try {
      // Load all settings in parallel
      const [goalsContent, focusContent, panelState] = await Promise.all([
        getSetting<string>(WEEKLY_PANELS_KEYS.GOALS_CONTENT, {
          namespace: WEEKLY_PANELS_NAMESPACE,
          defaultValue: '',
        }),
        getSetting<string>(WEEKLY_PANELS_KEYS.FOCUS_CONTENT, {
          namespace: WEEKLY_PANELS_NAMESPACE,
          defaultValue: '',
        }),
        getSetting<{ goalsPanel: PanelState; focusPanel: PanelState }>(
          WEEKLY_PANELS_KEYS.PANEL_STATE,
          {
            namespace: WEEKLY_PANELS_NAMESPACE,
            defaultValue: {
              goalsPanel: defaultPanelStates.goals,
              focusPanel: defaultPanelStates.focus,
            },
          }
        ),
      ]);

      // Get fresh state again in case it changed during async operation
      useWeeklyPanelsStore.getState().hydrate({
        goalsContent: goalsContent ?? '',
        focusContent: focusContent ?? '',
        goalsPanel: panelState?.goalsPanel ?? defaultPanelStates.goals,
        focusPanel: panelState?.focusPanel ?? defaultPanelStates.focus,
      });

      logger.log('[WeeklyPanels] Loaded panel data from PostgreSQL');
    } catch (error) {
      logger.error('[WeeklyPanels] Failed to load panel data:', error);
      useWeeklyPanelsStore.getState().setInitialized(true);
    } finally {
      useWeeklyPanelsStore.getState().setLoading(false);
    }
  }, []); // CRITICAL: Empty dependency array - uses getState() for fresh values

  /**
   * Save content with debounce
   * Uses getState() to avoid store dependency in callback
   */
  const saveContent = useCallback(
    (panelType: PanelType, content: string) => {
      // Update store immediately (optimistic) using getState()
      useWeeklyPanelsStore.getState().setContent(panelType, content);

      // Clear existing debounce for this panel
      const debounceKey = `content-${panelType}`;
      if (contentDebounceRef.current[debounceKey]) {
        clearTimeout(contentDebounceRef.current[debounceKey]);
      }

      // Debounce the save
      contentDebounceRef.current[debounceKey] = setTimeout(async () => {
        try {
          const key =
            panelType === 'goals'
              ? WEEKLY_PANELS_KEYS.GOALS_CONTENT
              : WEEKLY_PANELS_KEYS.FOCUS_CONTENT;

          await setSetting(key, content, {
            namespace: WEEKLY_PANELS_NAMESPACE,
          });

          logger.log(`[WeeklyPanels] Saved ${panelType} content`);
        } catch (error) {
          logger.error(`[WeeklyPanels] Failed to save ${panelType} content:`, error);
        }
      }, CONTENT_DEBOUNCE_MS);
    },
    [] // No dependencies - uses getState() for store access
  );

  /**
   * Save panel state (position + collapsed) with debounce
   */
  const savePanelState = useCallback(() => {
    // Clear existing debounce
    if (positionDebounceRef.current) {
      clearTimeout(positionDebounceRef.current);
    }

    // Debounce the save
    positionDebounceRef.current = setTimeout(async () => {
      try {
        const currentState = useWeeklyPanelsStore.getState();
        await setSetting(
          WEEKLY_PANELS_KEYS.PANEL_STATE,
          {
            goalsPanel: currentState.goalsPanel,
            focusPanel: currentState.focusPanel,
          },
          { namespace: WEEKLY_PANELS_NAMESPACE }
        );

        logger.log('[WeeklyPanels] Saved panel state');
      } catch (error) {
        logger.error('[WeeklyPanels] Failed to save panel state:', error);
      }
    }, POSITION_DEBOUNCE_MS);
  }, []);

  /**
   * Update position and trigger save
   * Uses getState() to avoid store dependency
   */
  const updatePosition = useCallback(
    (panelType: PanelType, position: Position) => {
      useWeeklyPanelsStore.getState().setPosition(panelType, position);
      savePanelState();
    },
    [savePanelState]
  );

  /**
   * Update size and trigger save
   * Uses getState() to avoid store dependency
   */
  const updateSize = useCallback(
    (panelType: PanelType, size: PanelSize) => {
      useWeeklyPanelsStore.getState().setSize(panelType, size);
      savePanelState();
    },
    [savePanelState]
  );

  /**
   * Toggle collapsed state and trigger save
   * Uses getState() to avoid store dependency
   */
  const toggleCollapsed = useCallback(
    (panelType: PanelType) => {
      useWeeklyPanelsStore.getState().toggleCollapsed(panelType);
      savePanelState();
    },
    [savePanelState]
  );

  /**
   * Set collapsed state and trigger save
   * Uses getState() to avoid store dependency
   */
  const setCollapsed = useCallback(
    (panelType: PanelType, collapsed: boolean) => {
      useWeeklyPanelsStore.getState().setCollapsed(panelType, collapsed);
      savePanelState();
    },
    [savePanelState]
  );

  // Auto-load on mount - CRITICAL: Empty dependency array prevents infinite loop
  useEffect(() => {
    loadPanels();

    // Cleanup debounce timers on unmount
    return () => {
      Object.values(contentDebounceRef.current).forEach(clearTimeout);
      if (positionDebounceRef.current) {
        clearTimeout(positionDebounceRef.current);
      }
    };
  }, []); // Empty deps - loadPanels is stable with empty deps

  return {
    // State
    goalsContent: store.goalsContent,
    focusContent: store.focusContent,
    goalsPanel: store.goalsPanel,
    focusPanel: store.focusPanel,
    isLoading: store.isLoading,
    isInitialized: store.isInitialized,

    // Actions
    saveContent,
    updatePosition,
    updateSize,
    toggleCollapsed,
    setCollapsed,
    loadPanels,
  };
};
